import { useState } from "react";
import { useAuth } from "@/hooks/auth";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { TimeSlot } from "@/hooks/useMentorScheduleAvailability";
import { format } from "date-fns";

interface UseScheduleMentorshipProps {
  mentorId: string;
  offeringId: string;
  gameId?: string;
  offeringTitle: string;
  price?: number;
}

export const useScheduleMentorship = ({
  mentorId,
  offeringId,
  gameId,
  offeringTitle,
  price = 100
}: UseScheduleMentorshipProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [isProcessing, setIsProcessing] = useState(false);
  
  const handleSchedule = async (selectedDate: Date, selectedTimeSlot: TimeSlot) => {
    if (!user) {
      toast({
        title: "Login necessário",
        description: "Faça login para agendar uma mentoria.",
        variant: "destructive",
      });
      return false;
    }
    
    if (!selectedDate || !selectedTimeSlot) {
      toast({
        title: "Erro",
        description: "Por favor, selecione uma data e horário disponíveis.",
        variant: "destructive",
      });
      return false;
    }

    try {
      setIsProcessing(true);
      
      // Format the selected date correctly to avoid timezone issues
      const formattedDate = format(selectedDate, 'yyyy-MM-dd');
      
      // Use the correct gameId or fetch from the offering
      let effectiveGameId = gameId;
      
      if (!effectiveGameId || effectiveGameId.trim() === '') {
        console.log("GameId not provided, fetching from offering:", offeringId);
        
        // Fetch the game_id from the mentorship offering
        const { data: offeringData, error: offeringError } = await supabase
          .from('mentorship_offerings')
          .select('game_id')
          .eq('id', offeringId)
          .single();
          
        if (!offeringError && offeringData && offeringData.game_id) {
          effectiveGameId = offeringData.game_id;
          console.log("Found game_id from offering:", effectiveGameId);
        } else {
          console.warn("Could not fetch game_id from offering:", offeringError);
          
          // Only as a last resort, if we can't find the game from the offering
          // This should rarely happen and indicates a data issue
          const { data: games } = await supabase
            .from('games')
            .select('id, name')
            .order('name')
            .limit(1);
            
          if (games && games.length > 0) {
            effectiveGameId = games[0].id;
            console.warn("Using fallback game:", games[0].name, "for offering:", offeringId);
          } else {
            throw new Error("Não foi possível determinar o jogo para esta mentoria. Entre em contato com o suporte.");
          }
        }
      }
      
      console.log("Creating session with:", {
        player_profile_id: user.id,
        mentor_id: mentorId,
        game_id: effectiveGameId,
        scheduled_date: formattedDate,
        scheduled_start: selectedTimeSlot.start,
        scheduled_end: selectedTimeSlot.end,
        price: price // Log the price being used - already in cents
      });
      
      // Insert the booking into the sessions table with explicit RLS
      const { data: sessionData, error } = await supabase.from('sessions').insert({
        player_profile_id: user.id,
        mentor_id: mentorId,
        game_id: effectiveGameId,
        offering_id: offeringId, // ADICIONAR OFFERING_ID AQUI
        scheduled_date: formattedDate,
        scheduled_start: selectedTimeSlot.start,
        scheduled_end: selectedTimeSlot.end,
        status: 'pending',
        is_group: false
      }).select().single();

      if (error) {
        console.error("Error creating session:", error);
        
        // Provide more helpful error messages based on error code
        if (error.code === '23502') {
          throw new Error("Campo obrigatório não preenchido. Verifique todos os campos.");
        } else if (error.code === '23503') {
          throw new Error("Erro de referência. ID do jogo, mentor ou jogador inválido.");
        } else if (error.code === '42501' || error.code === '42P01') {
          throw new Error("Erro de permissão. Verifique suas credenciais.");
        } else {
          throw error;
        }
      }
      
      if (!sessionData || !sessionData.id) {
        throw new Error("Falha ao criar a sessão");
      }

      console.log("Session created successfully:", sessionData);

      // Get the current auth token to pass to the edge function
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        throw new Error("Erro ao obter sessão de autenticação");
      }

      // Now create a Stripe checkout session with the current price
      const { data: checkoutData, error: checkoutError } = await supabase.functions.invoke("create-checkout-session", {
        body: {
          sessionId: sessionData.id,
          offeringId: offeringId,
          mentorId: mentorId,
          mentorshipTitle: offeringTitle,
          price: price // Price is already in cents from DB (price_cents)
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      });

      if (checkoutError) {
        throw new Error(`Erro ao criar sessão de pagamento: ${checkoutError.message}`);
      }

      if (!checkoutData || !checkoutData.url) {
        throw new Error("URL de checkout não recebida");
      }

      // Redirect to Stripe Checkout
      window.location.href = checkoutData.url;
      return true;

    } catch (error: any) {
      console.error('Error scheduling mentorship:', error);
      toast({
        title: "Erro ao agendar",
        description: `Ocorreu um erro ao agendar sua mentoria: ${error.message || 'Tente novamente.'}`,
        variant: "destructive",
      });
      setIsProcessing(false);
      return false;
    }
  };

  return {
    isProcessing,
    handleSchedule
  };
};
